/**
 * Offline Queue — IndexedDB-backed mutation queue for PWA offline support.
 *
 * When a critical API mutation (POST/PATCH/PUT) fails due to network error,
 * it's saved to IndexedDB and replayed when connectivity returns.
 *
 * Only queues mutation endpoints that are safe to retry (idempotent or
 * server-side dedup). GET requests are NOT queued — they simply fail.
 */

// ─── Types ──────────────────────────────────────────────────────────────────

export interface QueuedRequest {
  id: string;
  /** ISO timestamp when queued */
  queuedAt: string;
  /** HTTP method */
  method: string;
  /** Full URL path (relative to API base) */
  path: string;
  /** Serialized JSON body (or null for DELETE) */
  body: string | null;
  /** Number of replay attempts so far */
  attempts: number;
  /** ISO timestamp of the most recent replay attempt */
  lastAttemptAt?: string;
  /** ISO timestamp before which replay should not retry this item */
  nextRetryAt?: string;
  /** Last error message */
  lastError?: string;
  /** For file uploads: base64-encoded file data */
  fileBase64?: string;
  /** For file uploads: original filename */
  fileName?: string;
  /** For file uploads: MIME type */
  fileMime?: string;
  /** For file uploads: additional form fields */
  formFields?: Record<string, string>;
}

export type SyncStatus = 'idle' | 'syncing' | 'error';

type Listener = (pending: number, status: SyncStatus) => void;

// ─── Critical paths that should be queued when offline ──────────────────────

const QUEUEABLE_PATHS = [
  // TMS — driver mobile workflows
  '/tms/mobile/trips/',   // update-status, report-exception
  '/tms/mobile/pod-upload',  // POD photo & signature uploads
  // WMS — warehouse operations
  '/wms/asn/',              // ASN receive
  '/wms/inbound-orders/',   // inbound receive
  '/wms/putaway/',          // putaway task update
  '/wms/qc',                // QC submission
  '/wms/pick-lists/',       // pick confirmation
  '/wms/cycle-counts/',     // cycle count submit
  '/wms/pack-tasks/',       // pack confirmation
  '/wms/transfer-orders/',  // transfer execution
  '/wms/attachments',       // photo uploads
  '/wms/yard-gates',        // yard gate IN/OUT
  '/wms/stuffing-records',  // container stuffing CBM/weight/seal
  // Depot — on-site gate / inspection / M&R capture
  '/api/v1/depot/gate/check-in',
  '/api/v1/depot/gate/check-out',
  '/api/v1/depot/eir',
  '/api/v1/depot/inspections',
  '/api/v1/depot/moves',
  '/api/v1/depot/visits/',
  '/api/v1/depot/repair-estimates',
  '/api/v1/depot/repair-jobs',
  '/api/v1/depot/wash-jobs',
  // FMS — freight operations
  '/api/v1/shipments/',     // shipment status updates, confirmations
  '/api/v1/shipments/approve',
  '/api/v1/shipments/release',
  '/api/v1/shipments/close',
  // Finance — approval & acknowledgment
  '/api/v1/invoices/',      // invoice actions
  '/api/v1/payments/',      // payment recording
  '/api/v1/ledger/',        // ledger entry actions
  // Customer portal — self-service mutations
  '/api/selfservice/bookings',  // booking submissions
  // CRM/go-pwa — sales visit, follow-up, quote follow-up, confirmed channel send
  '/crm/pwa/',
  '/crm/follow-up-draft/confirm-send',
  // Actions — universal approve/reject/resend
  '/api/v1/actions/',       // action execution
];

function isQueueableMutation(method: string, path: string): boolean {
  if (method === 'GET') return false;
  return QUEUEABLE_PATHS.some(p => path.includes(p));
}

// ─── IndexedDB helpers ──────────────────────────────────────────────────────

const DB_NAME = 'boox_offline_queue';
const DB_VERSION = 1;
const STORE_NAME = 'requests';

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function getAllQueued(): Promise<QueuedRequest[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result as QueuedRequest[]);
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => db.close();
  });
}

async function addToQueue(item: QueuedRequest): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    store.put(item);
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
  });
}

async function removeFromQueue(id: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    store.delete(id);
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
  });
}

async function updateInQueue(item: QueuedRequest): Promise<void> {
  await addToQueue(item); // put() is upsert
}

// ─── Queue singleton ────────────────────────────────────────────────────────

let pendingCount = 0;
let syncStatus: SyncStatus = 'idle';
const listeners = new Set<Listener>();
let isSyncing = false;
let replayFetch: ((path: string, init: RequestInit) => Promise<Response>) | null = null;

const MAX_REPLAY_ATTEMPTS = 5;
const RETRY_BASE_DELAY_MS = 5_000;
const RETRY_MAX_DELAY_MS = 60_000;

function notifyListeners() {
  for (const fn of listeners) {
    try { fn(pendingCount, syncStatus); } catch { /* ignore */ }
  }
}

async function refreshCount() {
  try {
    const items = await getAllQueued();
    pendingCount = items.length;
  } catch {
    pendingCount = 0;
  }
}

/**
 * Check if a fetch error is a network failure (offline / DNS / timeout).
 */
export function isNetworkError(err: unknown): boolean {
  if (err instanceof TypeError && err.message.includes('fetch')) return true;
  if (err instanceof DOMException && err.name === 'AbortError') return false; // user-cancelled
  if (err instanceof TypeError) return true; // "Failed to fetch" / "NetworkError"
  return false;
}

/**
 * Attempt to queue a failed mutation request for later replay.
 * Returns true if queued, false if the request type is not queueable.
 */
export async function enqueueIfOffline(
  method: string,
  path: string,
  body: string | null,
  error: unknown,
): Promise<boolean> {
  if (!isNetworkError(error)) return false;
  if (!isQueueableMutation(method, path)) return false;

  const item: QueuedRequest = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    queuedAt: new Date().toISOString(),
    method,
    path,
    body,
    attempts: 0,
  };

  await addToQueue(item);
  pendingCount++;
  notifyListeners();
  return true;
}

/**
 * Queue a file upload (FormData) for offline replay.
 * Converts the File to base64 so it survives IndexedDB serialization.
 */
export async function enqueueFileUpload(
  path: string,
  file: File,
  formFields: Record<string, string>,
): Promise<void> {
  const base64 = await fileToBase64(file);
  const item: QueuedRequest = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    queuedAt: new Date().toISOString(),
    method: 'POST',
    path,
    body: null,
    attempts: 0,
    fileBase64: base64,
    fileName: file.name,
    fileMime: file.type || 'image/jpeg',
    formFields,
  };

  await addToQueue(item);
  pendingCount++;
  notifyListeners();
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Strip data URL prefix: "data:image/jpeg;base64,XXXX" → "XXXX"
      const idx = result.indexOf(',');
      resolve(idx >= 0 ? result.slice(idx + 1) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function base64ToFile(base64: string, name: string, mime: string): File {
  const bytes = atob(base64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  return new File([arr], name, { type: mime });
}

/**
 * Replay all queued requests. Called when connectivity returns.
 */
export async function replayQueue(
  fetchFn: (path: string, init: RequestInit) => Promise<Response>,
): Promise<{ synced: number; failed: number }> {
  if (isSyncing) return { synced: 0, failed: 0 };
  isSyncing = true;
  syncStatus = 'syncing';
  notifyListeners();

  const items = await getAllQueued();
  // Sort by queued time (FIFO)
  items.sort((a, b) => a.queuedAt.localeCompare(b.queuedAt));

  let synced = 0;
  let failed = 0;
  const now = Date.now();

  for (const item of items) {
    if (item.nextRetryAt) {
      const nextRetryAt = Date.parse(item.nextRetryAt);
      if (!Number.isNaN(nextRetryAt) && nextRetryAt > now) continue;
    }

    if ((item.attempts ?? 0) >= MAX_REPLAY_ATTEMPTS) {
      failed++;
      continue;
    }

    try {
      let res: Response;

      if (item.fileBase64 && item.fileName && item.fileMime) {
        // File upload — reconstruct FormData
        const file = base64ToFile(item.fileBase64, item.fileName, item.fileMime);
        const fd = new FormData();
        fd.append('file', file);
        if (item.formFields) {
          for (const [k, v] of Object.entries(item.formFields)) fd.append(k, v);
        }
        res = await fetchFn(item.path, { method: 'POST', body: fd });
      } else {
        const init: RequestInit = {
          method: item.method,
          headers: { 'Content-Type': 'application/json' },
        };
        if (item.body) init.body = item.body;
        res = await fetchFn(item.path, init);
      }

      if (res.ok || res.status === 409) {
        // 409 = duplicate / already processed — treat as success
        await removeFromQueue(item.id);
        synced++;
      } else if (res.status >= 400 && res.status < 500) {
        // Client error — request is invalid, remove (won't succeed on retry)
        await removeFromQueue(item.id);
        failed++;
      } else {
        // Server error — keep in queue for next attempt
        item.attempts++;
        item.lastAttemptAt = new Date().toISOString();
        item.nextRetryAt = new Date(Date.now() + getRetryDelayMs(item.attempts)).toISOString();
        item.lastError = `HTTP ${res.status}`;
        await updateInQueue(item);
        failed++;
      }
    } catch (err) {
      // Still offline — stop trying
      item.attempts++;
      item.lastAttemptAt = new Date().toISOString();
      item.nextRetryAt = new Date(Date.now() + getRetryDelayMs(item.attempts)).toISOString();
      item.lastError = err instanceof Error ? err.message : String(err);
      await updateInQueue(item);
      failed++;
      break; // No point continuing if we're still offline
    }
  }

  await refreshCount();
  syncStatus = failed > 0 ? 'error' : 'idle';
  isSyncing = false;
  notifyListeners();
  return { synced, failed };
}

/**
 * Subscribe to queue state changes.
 */
export function onQueueChange(fn: Listener): () => void {
  listeners.add(fn);
  // Emit current state immediately
  fn(pendingCount, syncStatus);
  return () => listeners.delete(fn);
}

/**
 * Get current pending count (sync).
 */
export function getPendingCount(): number {
  return pendingCount;
}

export function getSyncStatus(): SyncStatus {
  return syncStatus;
}

export async function flushOfflineQueue(): Promise<{ synced: number; failed: number }> {
  if (!replayFetch) throw new Error('Offline queue has not been initialized');
  return replayQueue(replayFetch);
}

/**
 * Initialize: load count from IndexedDB and set up online listener.
 */
export async function initOfflineQueue(
  fetchFn: (path: string, init: RequestInit) => Promise<Response>,
): Promise<void> {
  replayFetch = fetchFn;
  await refreshCount();
  notifyListeners();

  const triggerReplay = () => {
    if (!navigator.onLine || pendingCount === 0 || isSyncing) return;
    replayQueue(fetchFn).catch(console.error);
  };

  // Auto-replay when browser comes back online
  window.addEventListener('online', triggerReplay);

  // Retry when the tab becomes visible again after weak-network/background periods.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') triggerReplay();
  });

  // Periodic safety-net replay for long-running mobile sessions.
  window.setInterval(triggerReplay, 30_000);

  // If we're online and have pending items, replay now
  if (navigator.onLine && pendingCount > 0) {
    triggerReplay();
  }
}

function getRetryDelayMs(attempts: number): number {
  const exponent = Math.max(0, attempts - 1);
  return Math.min(RETRY_BASE_DELAY_MS * (2 ** exponent), RETRY_MAX_DELAY_MS);
}
